<template>
    <div>
        <Card class="simInfo-card">
            <div class="header">
                <div class="sim-info">
                    <img :src="ImgSimCard"/>
                    <div>{{ isLocked ? maskTitle : simManager.data.simCard?.simCardCode }}</div>
                    <div class="retired" v-if="boundStatus==='unbound' || simManager.isRetired">
                        {{ boundStatus==='unbound' ? $t('dashboard.unbinded') : $t('my_sim.retired') }}
                    </div>
                </div>
                <ReoBtnList
                    v-if="!isLocked"
                    :btnItems="[{text:$t('my_sim.unbind'),onClick:showConfirmUnbind}]"
                />
                <ReoCommonButton
                    v-if="!isApp && isLocked && currentSubscription?.planInfo?.series?.category === ESeriesCategory.CLOUD_STORAGE_DATA_PLAN"
                    :label="$t('homepage.cloud_library')"
                    to="/user/cloud-library"
                    color-type="light"
                    size="small"
                    class="btn"
                />
            </div>
            <ul class="detail" v-show="showItems.more">
                <li v-if="isLocked">
                    <ReoDescription
                        :title="$t('my_sim.model')"
                        :content="deviceInfo?.model.title"
                        style="margin-bottom:8px;"
                    />
                    <ReoDescription
                        :title="$t('my_sim.uid')"
                        :content="boundStatus==='unbound' ? desensitizeString(deviceInfo?.uid):deviceInfo?.uid"
                    />
                </li>
                <li v-else>
                    <ReoDescription
                        :title="$t('my_sim.sim_card_assigned')"
                        :content="$t('my_sim.iccid',{iccid: simManager.iccid})"
                        desensitize
                    />
                </li>
                <li v-if="!isLocked && showSimDeviceInfo">
                    <ReoDescription
                        :title="$t('my_sim.device_last_used')"
                        :content="currentDeviceString"
                    />
                </li>
            </ul>
            <ShowMoreButton
                v-model="showItems.more"
            />
            <ReoDialog
                :title="$t('my_sim.confirm_unbind.title')"
                :labelOfConfirm="$t('my_sim.confirm_unbind.confirm')"
                :labelOfCancel="$t('my_sim.confirm_unbind.cancel')"
                switchButton
                v-if="showItems.dialog.confirmUnbind"
                @cancel="showItems.dialog.confirmUnbind = false"
                @confirm="unbind"
            >
                <p>{{ $t('my_sim.confirm_unbind.p1') }}</p>
                <p>{{ $t('my_sim.confirm_unbind.p2') }}</p>
                <p>{{ $t('my_sim.confirm_unbind.p3') }}</p>
                <p class="warning">{{ $t('my_sim.unbind_warn') }}</p>
            </ReoDialog>
            <ReoDialog
                :title="$t('my_sim.unbind_success.title')"
                :labelOfConfirm="$t('cart.switch.ok')"
                v-if="showItems.dialog.unbindSuccess"
                @cancel="showItems.dialog.unbindSuccess = false"
                @confirm="toDashboard"
            >
                <p>{{ $t('my_sim.unbind_success.p1') }}</p>
                <p class="warning">{{ $t('my_sim.unbind_warn') }}</p>
            </ReoDialog>
            <InputPaypal
                v-model="showItems.inputPaypal"
                @confirmInputPaypal="unbind"
            />
        </Card>
    </div>

</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import Card from '@/basic/Card.vue';
import ImgSimCard from 'IMAGE/dashboard/simCard.png';
import { SimManager } from '@/lib/simManager';
import ShowMoreButton from '@/components/ShowMoreButton.vue';
import ReoDescription from './components/ReoDescription.vue';
import ReoBtnList from './components/ReoBtnList.vue';
import ReoDialog from '@/basic/Dialog.vue';
import { deleteSim } from '@/api/sim';
import { unfoldError } from '@/common/js/notice';
import { extractErrorInfoToString, getErrorSymbol } from '@/common/js/utils';
import InputPaypal from '@/components/InputPaypal.vue';
import { config } from 'CONFIG';
import { BoundStatus, ICloudService, IReolinkDevice, ISubDevice, ESeriesCategory } from '@reolink-cloud/interface';
import { desensitizeString } from '@/filter';
import { getDeviceTitle, isApp } from '@/utils/business';

export default Vue.extend({
    props: {
        deviceInfo: Object as PropType<IReolinkDevice>,
        simManager: Object as PropType<SimManager>,
        subDevice: Object as PropType<ISubDevice>,
    },
    components: {
        Card,
        ShowMoreButton,
        ReoDescription,
        ReoBtnList,
        ReoDialog,
        InputPaypal,
    },
    data() {

        return {
            ImgSimCard,
            showItems: {
                dialog: {
                    confirmUnbind: false,
                    unbindSuccess: false
                },
                more: false,
                inputPaypal: false,
                iccid: false
            },
            ESeriesCategory,
            isApp
        };

    },
    mounted() {

        this.boundStatus = this.$route.query['boundStatus'] as BoundStatus;

    },
    computed: {
        currentDeviceString(): string  {

            return this.simManager.deviceString;

        },
        showSimDeviceInfo(): boolean {

            return config.showSimDeviceInfo;

        },
        maskTitle(): string {

            if ( this.boundStatus === 'unbound'  ) {

                return '***';

            } else  {

                return getDeviceTitle( this.deviceInfo );

            }

        },
        isLocked(): boolean {

            return !!this.simManager?.data.esimDevice;

        },
        currentSubscription(): ICloudService | undefined {

            return this.simManager.currentSubscription;

        },
        boundStatus(): BoundStatus | undefined {

            return this.deviceInfo?.status;
        
        }

    },
    methods: {
        desensitizeString,
        showConfirmUnbind() {

            this.showItems.dialog.confirmUnbind = true;

        },
        async unbind( account?: string ) {

            try {

                await deleteSim({ iccid: this.simManager.iccid ?? '',
                    manualRefundInfo: account ? {
                        method: 'paypal',
                        account
                    } : undefined });

                this.showItems.dialog.confirmUnbind = false;
                this.showItems.dialog.unbindSuccess = true;

            } catch ( e ) {

                const symbol = getErrorSymbol( e );
                const reason = extractErrorInfoToString( e, 'reason' );
                let errorKey = 'errors.server_error';
                switch ( symbol ) {

                    case 'payer_account_required':
                        this.showItems.inputPaypal = true;
                        errorKey = '';
                        break;
                    case 'usr_locked': {

                        errorKey = reason === 'user locked for auto-renew' ? 'errors.user_locked_for_auto-renew' : 'errors.usr_locked';
    
                        break;

                    }
                    case 'cannot_cancel_subscription': {

                        if ( reason === 'service is refunding' ) {

                            errorKey = 'errors.service_refunding';

                        }
                        break;
                    
                    }
                    default:
                        console.error( e );

                }
                if ( errorKey ) {

                    unfoldError( errorKey );
                
                }

                this.showItems.dialog.confirmUnbind = false;

            }

        },
        async toDashboard() {

            await this.$router.push({
                path: '/user/dashboard'
            });

        }
    }
});
</script>

<style lang="less" scoped>
.simInfo-card {
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    gap: 24px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .sim-info {
            display: flex;
            gap: 12px;
            align-items: center;
            font-size: 20px;
            font-weight: 600;
            color: @font_primary;
            .retired{
                background-color: @red_5;
                color: @white;
                padding: 4px;
                border-radius: 4px;
            }
        }
    }

    ul.detail {
        display: flex;
        flex-direction: column;
        gap: 24px;
        margin-top: 12px;
    }

}
</style>
